/**
 * TelesalesDashboard — focused dashboard for `agent` and `telesales_director`.
 *
 * They never see project tiles, WP-Katalog, Pönalen, Sales-Stats etc.
 * Instead they get a clean home with their Telesales-specific KPIs and
 * direct entry points.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Phone, ListChecks, Users2, ClipboardList, CalendarIcon, Receipt,
  Trophy, Inbox, Crown, ArrowRight, GraduationCap, CalendarClock, Clock,
  Bell, BellOff,
} from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { PlacetelCallButton } from "@/components/PlacetelCallButton";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantCapabilities } from "@/contexts/TenantCapsContext";
import SubmissionDetailModal from "@/components/SubmissionDetailModal";
import { pushStatus, subscribePush, unsubscribePush, pushSupported } from "@/lib/pushClient";
import api from "@/lib/api";

const STATUS_LABEL = {
  pending_review: "Direktor-Freigabe",
  new: "Neu",
  routed: "An Vertrieb",
  archived: "Archiviert",
  sent_back: "Zurück an Agent",
  in_auction: "Auktion",
  assigned: "Zugewiesen",
};
const STATUS_COLOR = {
  pending_review: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  new: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  routed: "bg-[#00E575]/15 text-[#00E575] border-[#00E575]/40",
  archived: "bg-zinc-700/40 text-zinc-400 border-zinc-700",
  sent_back: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  assigned: "bg-[#00E575]/15 text-[#00E575] border-[#00E575]/40",
  in_auction: "bg-violet-500/15 text-violet-300 border-violet-500/40",
};


function StatTile({ icon: Icon, label, value, accent = "text-zinc-300", testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 hover:border-zinc-700 transition"
    >
      <div className="flex items-start justify-between mb-2">
        <Icon className={accent} size={20} />
      </div>
      <div className="font-display text-3xl font-bold tracking-tight leading-none">{value}</div>
      <div className="text-[12px] text-zinc-400 uppercase tracking-wider mt-1.5">{label}</div>
    </div>
  );
}

function NavTile({ to, icon: Icon, label, sub, testid, accent = "" }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group bg-[#18181B] border border-zinc-800 rounded-2xl p-4 flex items-center gap-3 hover:border-[#00E575]/50 active:scale-[0.99] transition ${accent}`}
    >
      <div className="h-10 w-10 rounded-xl bg-[#00E575]/15 flex items-center justify-center shrink-0">
        <Icon className="text-[#00E575]" size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-bold text-[13px] truncate">{label}</div>
        {sub && <div className="text-[12px] text-zinc-400 truncate">{sub}</div>}
      </div>
      <ArrowRight size={14} className="text-zinc-500 group-hover:text-[#00E575] transition" />
    </Link>
  );
}


export default function TelesalesDashboard() {
  const { user } = useAuth();
  const { has: hasCap } = useTenantCapabilities();
  const navigate = useNavigate();
  const isDirector = user?.role === "telesales_director";

  const [submissions, setSubmissions] = useState([]);
  const [teamSize, setTeamSize] = useState(0);
  const [wiedervorlagen, setWiedervorlagen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [pushState, setPushState] = useState(() => ({
    supported: pushSupported(),
    subscribed: false,
    permission: typeof Notification !== "undefined" ? Notification.permission : "default",
  }));
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    if (pushSupported()) pushStatus().then(setPushState);
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushState.subscribed) {
        await unsubscribePush();
        toast.success("Push-Erinnerungen deaktiviert");
      } else {
        await subscribePush();
        toast.success("Push-Erinnerungen aktiv. Du wirst 15 Min vor jeder Wiedervorlage benachrichtigt.");
      }
      const next = await pushStatus();
      setPushState(next);
    } catch (e) {
      toast.error(e.message || "Konnte Push nicht ändern");
    } finally { setPushBusy(false); }
  };

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      api.get("/telesales/submissions?limit=200").then((r) => r.data || []).catch(() => []),
      isDirector
        ? api.get("/telesales/team").then((r) => r.data || []).catch(() => [])
        : Promise.resolve([]),
      api.get("/telesales/wiedervorlagen?limit=50").then((r) => r.data || []).catch(() => []),
    ]).then(([subs, team, wv]) => {
      setSubmissions(subs);
      setTeamSize(team.length);
      setWiedervorlagen(wv);
      setLoading(false);
    });
  };

  useEffect(() => { loadAll(); }, [isDirector]);

  // KPI calculations
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfDay = new Date(startOfDay);
  endOfDay.setDate(endOfDay.getDate() + 1);
  const startOfWeek = new Date(startOfDay);
  startOfWeek.setDate(startOfDay.getDate() - ((startOfDay.getDay() + 6) % 7)); // Monday
  const todayCount = submissions.filter((s) => s.received_at && new Date(s.received_at) >= startOfDay).length;
  const weekCount  = submissions.filter((s) => s.received_at && new Date(s.received_at) >= startOfWeek).length;
  const pendingCount = submissions.filter((s) => s.status === "pending_review").length;
  const routedCount  = submissions.filter((s) => s.status === "routed" || s.status === "assigned").length;
  const recent = submissions.slice(0, 5);

  // „Heute fällig"-Banner: Wiedervorlagen mit Datum <= heute Ende
  const dueToday = wiedervorlagen.filter((w) => {
    if (!w.wiedervorlage_at) return false;
    const d = new Date(w.wiedervorlage_at);
    return d < endOfDay;
  });
  const overdueCount = dueToday.filter((w) => new Date(w.wiedervorlage_at) < startOfDay).length;

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="telesales-dashboard">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-3 sm:px-4 py-5 sm:py-7">
        <div className="mb-6 flex items-center gap-3">
          {isDirector ? (
            <Crown className="text-amber-400" size={28} />
          ) : (
            <Phone className="text-[#00E575]" size={28} />
          )}
          <div>
            <p className="font-mono text-[12px] text-[#00E575] tracking-[0.3em] uppercase mb-0.5">
              {isDirector ? "Telesales-Direktor" : "Telesales-Agent"}
            </p>
            <h1 className="font-display text-2xl sm:text-4xl font-bold tracking-tight">
              Hallo {user?.first_name}.
            </h1>
            <p className="text-zinc-400 text-[13px] mt-1">
              {isDirector
                ? "Dein Team auf einen Blick — alle Submissions, Pending-Reviews, Call-Listen."
                : "Schnell zum nächsten Anruf — erfasse Leads in Sekunden."}
            </p>
          </div>
        </div>

        {/* „Heute fällig"-Banner — dezent, aber sichtbar. Erscheint nur, wenn
            Wiedervorlagen heute oder überfällig sind. */}
        {!loading && dueToday.length > 0 && (
          <button
            type="button"
            onClick={() => {
              const el = document.querySelector('[data-testid="wiedervorlagen-widget"]');
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            data-testid="due-today-banner"
            className={`w-full mb-5 px-4 py-3 rounded-xl border text-left flex items-center justify-between gap-3 transition hover:bg-amber-500/[0.07] ${
              overdueCount > 0
                ? "bg-amber-500/[0.04] border-amber-500/30 hover:border-amber-500/50"
                : "bg-zinc-900/40 border-zinc-700/60 hover:border-zinc-600"
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <CalendarClock
                className={overdueCount > 0 ? "text-amber-400 shrink-0" : "text-zinc-400 shrink-0"}
                size={18}
              />
              <div className="min-w-0">
                <div className={`text-[13px] font-semibold ${overdueCount > 0 ? "text-amber-200" : "text-zinc-200"}`}>
                  {dueToday.length === 1
                    ? "1 Wiedervorlage heute fällig"
                    : `${dueToday.length} Wiedervorlagen heute fällig`}
                  {overdueCount > 0 && (
                    <span className="ml-2 text-[12px] font-mono uppercase tracking-wider text-amber-300/90">
                      · {overdueCount} überfällig
                    </span>
                  )}
                </div>
                <div className="text-[11.5px] text-zinc-500 mt-0.5 truncate">
                  {dueToday.slice(0, 3).map((w) => w.last_name || w.first_name || "?").join(" · ")}
                  {dueToday.length > 3 && ` +${dueToday.length - 3}`}
                </div>
              </div>
            </div>
            <span className="text-[12px] text-zinc-300 uppercase tracking-widest shrink-0 inline-flex items-center gap-1">
              Anzeigen <ArrowRight size={11} />
            </span>
          </button>
        )}

        {/* Primary CTA */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
          <Link
            to="/telesales?tab=capture"
            data-testid="dashboard-telesales-capture-cta"
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#00E575] to-[#00C964] text-[#09090B] rounded-2xl px-4 h-14 active:scale-[0.99] transition glow-green hover:shadow-[0_0_20px_rgba(0,229,117,0.35)] font-bold text-[15px]"
          >
            <Phone size={18} /> Anruf erfassen
          </Link>
          {!isDirector && hasCap("telesales_capture") && (
            <Link
              to="/telesales/next-call"
              data-testid="dashboard-next-call-cta"
              className="flex items-center justify-center gap-2 bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/50 text-zinc-100 rounded-2xl px-4 h-14 active:scale-[0.99] transition font-bold text-[14px]"
            >
              <Phone size={16} className="text-[#00E575]" /> Nächster Anruf (Pool)
            </Link>
          )}
          {isDirector && (
            <Link
              to="/telesales/director"
              data-testid="dashboard-pending-review-cta"
              className="relative flex items-center justify-center gap-2 bg-[#18181B] border border-zinc-800 hover:border-amber-500/50 text-zinc-100 rounded-2xl px-4 h-14 active:scale-[0.99] transition font-bold text-[14px]"
            >
              <Inbox size={16} className="text-amber-400" /> Pending-Review
              {pendingCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-amber-500 text-[#09090B] text-[12px] font-bold">
                  {pendingCount}
                </span>
              )}
            </Link>
          )}
        </div>

        {/* KPI Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-6">
          <StatTile
            testid="stat-telesales-today"
            icon={Phone}
            label={isDirector ? "Team heute" : "Heute"}
            value={loading ? "…" : todayCount}
            accent="text-[#00E575]"
          />
          <StatTile
            testid="stat-telesales-week"
            icon={ListChecks}
            label={isDirector ? "Team diese Woche" : "Diese Woche"}
            value={loading ? "…" : weekCount}
            accent="text-sky-400"
          />
          <StatTile
            testid="stat-telesales-pending"
            icon={Inbox}
            label="In Freigabe"
            value={loading ? "…" : pendingCount}
            accent="text-amber-400"
          />
          {isDirector ? (
            <StatTile
              testid="stat-team-size"
              icon={Users2}
              label="Aktive Agents"
              value={loading ? "…" : teamSize}
              accent="text-violet-400"
            />
          ) : (
            <StatTile
              testid="stat-telesales-routed"
              icon={Trophy}
              label="An Vertrieb"
              value={loading ? "…" : routedCount}
              accent="text-[#00E575]"
            />
          )}
        </div>

        {/* Wiedervorlagen Widget — sortiert nach Datum/Uhrzeit (chronologisch).
            Erscheint nur, wenn Wiedervorlagen existieren. */}
        {wiedervorlagen.length > 0 && (
          <section className="mb-6" data-testid="wiedervorlagen-widget">
            <div className="flex items-center justify-between mb-2 gap-2">
              <h2 className="font-display text-base font-bold text-zinc-300 inline-flex items-center gap-2">
                <CalendarClock size={16} className="text-amber-400" />
                Anstehende Wiedervorlagen
                <span className="text-[12px] text-zinc-300 font-normal">
                  ({wiedervorlagen.length})
                </span>
              </h2>
              {pushState.supported && (
                <button
                  type="button"
                  onClick={togglePush}
                  disabled={pushBusy}
                  data-testid="push-toggle-btn"
                  title={pushState.subscribed
                    ? "Push-Erinnerungen sind aktiv"
                    : "Push-Erinnerungen aktivieren (15 Min Vorlauf)"}
                  className={`shrink-0 inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-[12px] font-bold border transition ${
                    pushState.subscribed
                      ? "bg-[#00E575]/15 text-[#00E575] border-[#00E575]/40 hover:bg-[#00E575]/20"
                      : "bg-zinc-900 text-zinc-300 border-zinc-700 hover:border-zinc-500"
                  } disabled:opacity-50`}
                >
                  {pushState.subscribed ? <Bell size={12} /> : <BellOff size={12} />}
                  {pushState.subscribed ? "Erinnerungen an" : "Erinnerungen aktivieren"}
                </button>
              )}
            </div>
            <div className="space-y-1.5">
              {wiedervorlagen.slice(0, 6).map((w) => {
                const dt = w.wiedervorlage_at ? new Date(w.wiedervorlage_at) : null;
                const overdue = dt && dt < new Date();
                const isToday = dt && dt.toDateString() === new Date().toDateString();
                return (
                  <button
                    key={w.id}
                    type="button"
                    onClick={() => setActiveId(w.id)}
                    data-testid={`wiedervorlage-${w.id}`}
                    className={`w-full text-left bg-[#18181B] border rounded-xl px-3 py-2.5 hover:border-amber-500/50 transition flex items-center gap-3 ${
                      overdue ? "border-red-500/40" : isToday ? "border-amber-500/40" : "border-zinc-800"
                    }`}
                  >
                    <div className={`shrink-0 h-9 w-9 rounded-lg flex items-center justify-center ${
                      overdue ? "bg-red-500/15 text-red-300" : isToday ? "bg-amber-500/15 text-amber-300" : "bg-zinc-800 text-zinc-400"
                    }`}>
                      <Clock size={14} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-[13px] truncate">
                          {w.first_name} {w.last_name}
                        </span>
                        {overdue && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest bg-red-500/20 text-red-300">
                            überfällig
                          </span>
                        )}
                        {!overdue && isToday && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest bg-amber-500/20 text-amber-300">
                            heute
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] text-zinc-300 truncate">
                        {dt && dt.toLocaleString("de-DE", {
                          weekday: "short", day: "2-digit", month: "2-digit",
                          hour: "2-digit", minute: "2-digit",
                        })}
                        {w.phone && <span className="ml-2 font-mono">{w.phone}</span>}
                        {isDirector && w.agent_name && <span className="ml-2">· {w.agent_name}</span>}
                      </div>
                      {w.wiedervorlage_note && (
                        <div className="text-[12px] text-zinc-400 truncate mt-0.5">„{w.wiedervorlage_note}&ldquo;</div>
                      )}
                    </div>
                    {w.phone && (
                      <div onClick={(e) => e.stopPropagation()} className="shrink-0">
                        <PlacetelCallButton phone={w.phone} leadId={w.id} size="sm" variant="icon" />
                      </div>
                    )}
                    <ArrowRight size={14} className="shrink-0 text-zinc-500" />
                  </button>
                );
              })}
              {wiedervorlagen.length > 6 && (
                <div className="text-[12px] text-zinc-300 text-center pt-1">
                  + {wiedervorlagen.length - 6} weitere
                </div>
              )}
            </div>
          </section>
        )}

        {/* Quick-Navigation Tiles */}
        <h2 className="font-display text-base font-bold mb-2 text-zinc-300">Schnellzugriff</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-7">
          <NavTile
            testid="tile-telesales-home"
            to="/telesales"
            icon={Phone}
            label="Telesales-Center"
            sub={isDirector ? "Erfassen · Team · Erfassungen" : "Erfassen · Meine Erfassungen"}
          />
          {isDirector && (
            <NavTile
              testid="tile-pending-review"
              to="/telesales/director"
              icon={Inbox}
              label="Pending-Review-Queue"
              sub={`${pendingCount} ${pendingCount === 1 ? "Lead wartet" : "Leads warten"} auf Freigabe`}
            />
          )}
          {isDirector && hasCap("telesales_lists") && (
            <NavTile
              testid="tile-call-lists"
              to="/admin/call-lists"
              icon={ClipboardList}
              label="Call-Listen"
              sub="Kampagnen & Adress-Pools"
            />
          )}
          <NavTile
            testid="tile-calendar"
            to="/calendar"
            icon={CalendarIcon}
            label="Kalender"
            sub="Termine & Wiedervorlagen"
          />
          {hasCap("training_mode") && (
            <NavTile
              testid="tile-quiz"
              to="/quiz"
              icon={GraduationCap}
              label="Schulungsquiz"
              sub="Wissens-Check"
            />
          )}
          <NavTile
            testid="tile-abo"
            to="/abo"
            icon={Receipt}
            label="Mein Abo"
            sub="Rechnungen & Verbrauch"
          />
        </div>

        {/* Recent submissions */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-base font-bold text-zinc-300">
            {isDirector ? "Letzte Erfassungen im Team" : "Letzte eigene Erfassungen"}
          </h2>
          <Link
            to="/telesales?tab=submissions"
            data-testid="dashboard-all-submissions-link"
            className="text-[12px] text-[#00E575] hover:underline inline-flex items-center gap-1 font-medium"
          >
            Alle ansehen <ArrowRight size={12} />
          </Link>
        </div>
        {loading ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-6 text-center text-zinc-500 text-[13px]">
            Lade…
          </div>
        ) : recent.length === 0 ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center" data-testid="dashboard-empty-submissions">
            <Phone className="mx-auto text-zinc-600 mb-2" size={28} />
            <p className="text-zinc-400 text-[13px] mb-3">
              {isDirector ? "Noch keine Erfassungen vom Team." : "Noch keine Erfassungen."}
            </p>
            <button
              onClick={() => navigate("/telesales?tab=capture")}
              className="h-10 px-4 rounded-xl bg-[#00E575] text-[#09090B] font-bold text-[12px] hover:bg-[#00C964]"
            >
              Ersten Lead erfassen
            </button>
          </div>
        ) : (
          <div className="space-y-1.5">
            {recent.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActiveId(s.id)}
                data-testid={`dashboard-submission-${s.id}`}
                className="w-full text-left bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/50 rounded-xl px-4 py-3 transition"
              >
                <div className="flex items-center gap-3">
                  <span className={`shrink-0 px-2 py-0.5 rounded text-[12px] font-bold uppercase tracking-widest border ${
                    STATUS_COLOR[s.status || "new"] || "bg-zinc-700/40 text-zinc-300 border-zinc-700"
                  }`}>
                    {STATUS_LABEL[s.status || "new"] || s.status}
                  </span>
                  {/* Mail-Status-Badge (neueste Zustellung gewinnt: last_email_delivery > email_delivery).
                      Grün = Mail erfolgreich beim Kunden eingegangen, Rot = Fehler/zurückgewiesen. */}
                  {(() => {
                    const latest = s.last_email_delivery || s.email_delivery;
                    if (!latest) return null;
                    return latest.sent ? (
                      <span
                        data-testid={`dashboard-mail-ok-${s.id}`}
                        title={`Orientierungsangebot versendet an ${s.email || "Kunde"}`}
                        className="shrink-0 px-2 py-0.5 rounded text-[12px] font-bold uppercase tracking-widest bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/40"
                      >
                        Mail OK
                      </span>
                    ) : (
                      <span
                        data-testid={`dashboard-mail-fail-${s.id}`}
                        title={`Mail-Fehler: ${latest.reason || latest.error || "—"}`}
                        className="shrink-0 px-2 py-0.5 rounded text-[12px] font-bold uppercase tracking-widest bg-rose-500/15 text-rose-300 border border-rose-500/40"
                      >
                        Mail-Fehler
                      </span>
                    );
                  })()}
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-[13px] truncate">
                      {s.first_name} {s.last_name}
                    </div>
                    <div className="text-[12px] text-zinc-300 truncate">
                      {isDirector && s.agent_name && <span>{s.agent_name} · </span>}
                      {s.phone || s.email} {s.city ? `· ${s.city}` : ""}
                    </div>
                  </div>
                  <span className="hidden sm:inline text-[12px] text-zinc-300 font-medium">
                    {s.received_at && new Date(s.received_at).toLocaleDateString("de-DE")}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>
      {activeId && (
        <SubmissionDetailModal
          submissionId={activeId}
          onClose={() => setActiveId(null)}
          onSaved={() => loadAll()}
        />
      )}
    </div>
  );
}
